import { useState } from 'react';

const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const formatViews = (count) => {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
  return count.toLocaleString();
};

const CompetitorPage = ({ buildUrl, user, isPro }) => {
  const [query, setQuery] = useState('');
  const [competitors, setCompetitors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setCompetitors(null);
    try {
      const res = await fetch(buildUrl(`/competitor-titles?keywords=${encodeURIComponent(trimmed)}`), {
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user?.email || '',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCompetitors(data.competitors || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Competitor Titles</h1>
        <p style={styles.subtitle}>
          Search any topic or keyword to see the top-performing YouTube titles and how they compare.
        </p>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchBar}>
          <svg style={styles.searchIcon} viewBox="0 0 24 24" fill="#6b7280">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a topic, keyword, or title to search..."
            style={styles.input}
          />
          <button
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            style={{
              ...styles.searchButton,
              opacity: loading || !query.trim() ? 0.5 : 1,
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading && (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingDots}>
            <span style={styles.loadingDot} />
            <span style={{ ...styles.loadingDot, animationDelay: '0.2s' }} />
            <span style={{ ...styles.loadingDot, animationDelay: '0.4s' }} />
          </div>
          <p style={{ margin: '8px 0 0', color: '#a78bfa', fontSize: '14px' }}>
            Searching YouTube for top titles...
          </p>
        </div>
      )}

      {competitors && competitors.length === 0 && !loading && (
        <div style={styles.emptyState}>
          No results found. Try different keywords.
        </div>
      )}

      {competitors && competitors.length > 0 && (
        <div style={styles.resultsContainer}>
          <div style={styles.resultsHeader}>
            <span style={styles.resultsBadge}>YT</span>
            <span style={styles.resultsTitle}>Top {competitors.length} Videos by Views</span>
          </div>
          <div style={styles.resultsList}>
            {competitors.map((c, i) => (
              <a
                key={c.videoId}
                href={`https://www.youtube.com/watch?v=${c.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.resultItem}
                className="competitor-item"
              >
                <span style={styles.rank}>{i + 1}</span>
                <img
                  src={c.thumbnail}
                  alt=""
                  style={styles.thumbnail}
                />
                <div style={styles.resultInfo}>
                  <div style={styles.resultTitle}>{c.title}</div>
                  <div style={styles.resultChannel}>{c.channelTitle}</div>
                </div>
                <div style={styles.resultViews}>
                  <div style={styles.viewCount}>{formatViews(c.viewCount)} views</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .competitor-item:hover {
          border-color: rgba(251,146,60,0.3) !important;
          background: rgba(255,255,255,0.05) !important;
        }
        @keyframes competitor-dot-pulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '48px 24px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px',
    color: '#e2e8f0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
    lineHeight: '1.5',
  },
  searchContainer: {
    marginBottom: '24px',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '4px 4px 4px 16px',
    transition: 'border-color 0.2s',
  },
  searchIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    color: '#e2e8f0',
    fontSize: '15px',
    padding: '12px',
    fontFamily: 'inherit',
  },
  searchButton: {
    padding: '10px 24px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.2s',
    flexShrink: 0,
  },
  error: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '10px',
    padding: '12px 16px',
    color: '#fca5a5',
    fontSize: '14px',
    marginBottom: '20px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '48px 0',
  },
  loadingDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
  },
  loadingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#a78bfa',
    animation: 'competitor-dot-pulse 1.4s ease-in-out infinite',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 0',
    color: '#64748b',
    fontSize: '15px',
  },
  resultsContainer: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  resultsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  resultsBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(251, 146, 60, 0.15)',
    color: '#fb923c',
  },
  resultsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#94a3b8',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'border-color 0.2s, background 0.2s',
  },
  rank: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '700',
    minWidth: '24px',
    textAlign: 'center',
  },
  thumbnail: {
    width: '100px',
    height: '56px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  resultInfo: {
    flex: 1,
    minWidth: 0,
  },
  resultTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#e2e8f0',
    lineHeight: '1.4',
  },
  resultChannel: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '3px',
  },
  resultViews: {
    textAlign: 'right',
    flexShrink: 0,
  },
  viewCount: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#fb923c',
  },
};

export default CompetitorPage;
